import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useTheme } from "./ThemeContext";

/* ─── Icônes inline SVG ───────────────────────────────────────────────── */
function Icon({ name, color, size = 20 }) {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none" };
  switch (name) {
    case "markets":
      return (
        <svg {...common}>
          <path d="M4 19V13M10 19V9M16 19V5M22 19v3" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" transform="translate(0,-3)" />
          <rect x="3" y="16" width="3" height="5" rx="1" stroke={color} strokeWidth="1.8" fill="none" />
          <rect x="9" y="11" width="3" height="10" rx="1" stroke={color} strokeWidth="1.8" fill="none" />
          <rect x="15" y="7" width="3" height="14" rx="1" stroke={color} strokeWidth="1.8" fill="none" />
        </svg>
      );
    case "discussion":
      return (
        <svg {...common}>
          <path
            d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-4 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z"
            stroke={color} strokeWidth="1.8" strokeLinejoin="round" fill="none"
          />
          <circle cx="8" cy="11" r="1" fill={color} />
          <circle cx="12" cy="11" r="1" fill={color} />
          <circle cx="16" cy="11" r="1" fill={color} />
        </svg>
      );
    case "trade":
      return (
        <svg {...common}>
          <path d="M7 7h11M14 3l4 4-4 4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M17 17H6M10 21l-4-4 4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "lock":
      return (
        <svg {...common}>
          <rect x="5" y="11" width="14" height="9" rx="2" stroke={color} strokeWidth="2" fill="none" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
        </svg>
      );
    default:
      return null;
  }
}

/* ─── Traductions ──────────────────────────────────────────────────────── */
const LABELS = {
  markets:    { fr:"Marchés",    en:"Markets",  es:"Mercados",  de:"Märkte",    pt:"Mercados",  ar:"الأسواق" },
  discussion: { fr:"Discussion", en:"Discuss",  es:"Discusión", de:"Diskussion", pt:"Discussão", ar:"المناقشة" },
  trade:      { fr:"Trader",     en:"Trade",    es:"Operar",    de:"Handeln",   pt:"Negociar",  ar:"تداول" },
  closed:     { fr:"Fermé",      en:"Closed",   es:"Cerrado",   de:"Geschlossen", pt:"Fechado", ar:"مغلق" },
};
function tr(key, lang) { return LABELS[key]?.[lang] ?? LABELS[key]?.fr ?? key; }

/* ─── Styled components ───────────────────────────────────────────────── */
const fadeUp = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const pressDown = keyframes`from{transform:scale(1)}to{transform:scale(.92)}`;

const Container = styled.div`
  display: none;
  position: fixed;
  left: 0; right: 0; bottom: 0;
  z-index: 200;
  align-items: center;
  justify-content: space-between;
  gap: .5rem;
  background: ${({theme}) => theme.bgCard};
  border-top: 1px solid ${({theme}) => theme.border};
  padding: .6rem .6rem max(.6rem, env(safe-area-inset-bottom));
  box-shadow: 0 -2px 6px rgba(0,0,0,.18);
  animation: ${fadeUp} .2s ease;

  @media(max-width: 720px) {
    display: flex;
  }
`;

const TabBtn = styled.button`
  flex: ${({$primary}) => $primary ? "1.5" : "1"};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .25rem;
  padding: .55rem .35rem;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  background: ${({$primary, theme}) => $primary ? theme.green : "transparent"};
  opacity: ${({$disabled}) => $disabled ? .5 : 1};
  pointer-events: ${({$disabled}) => $disabled ? "none" : "auto"};
  transition: background .15s, opacity .15s;
  -webkit-tap-highlight-color: transparent;

  &:active {
    animation: ${pressDown} .09s ease forwards;
  }
`;

const IconWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -10px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  background: ${({theme}) => theme.red || "#ff5d5d"};
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
`;

const TabLabel = styled.span`
  font-size: ${({$primary}) => $primary ? ".72rem" : ".68rem"};
  font-weight: ${({$primary}) => $primary ? "800" : "600"};
  color: ${({$primary, $disabled, theme}) =>
    $primary ? "#000" : $disabled ? theme.textMuted : theme.text};
  white-space: nowrap;
`;

/* ════════════════════════════════════════════════════════════════════════
   MarketDetailBottomBar
════════════════════════════════════════════════════════════════════════
Props :
  - onTrade()       : callback bouton Trader (ouvre TradeModal, ou redirige login)
  - onDiscussion()  : callback bouton Discussion (scroll vers CommentsSection)
  - onMarkets()     : callback bouton Marchés (retour à la liste)
  - commentCount    : nombre de commentaires pour le badge
  - isOpen          : true si le marché accepte encore les trades
  - lang            : code langue
════════════════════════════════════════════════════════════════════════ */
export default function MarketDetailBottomBar({
  onTrade,
  onDiscussion,
  onMarkets,
  commentCount = 0,
  isOpen = true,
  lang = "fr",
})

{
    const { isDark } = useTheme();

  return (
    <Container>
      <TabBtn onClick={onMarkets}>
        <IconWrap>
          <Icon name="markets" color={isDark? "#e8edf2" : "#333"} />
        </IconWrap>
        <TabLabel>{tr("markets", lang)}</TabLabel>
      </TabBtn>

{ false &&       <TabBtn onClick={onDiscussion}>
        <IconWrap>
          <Icon name="discussion" color="#e8edf2" />
          {false & commentCount  > 0 && (
            <Badge>{commentCount > 99 ? "99+" : commentCount}</Badge>
          )}
        </IconWrap>
        <TabLabel>{tr("discussion", lang)}</TabLabel>
      </TabBtn>}

      <TabBtn
        $primary={isOpen}
        $disabled={!isOpen}
        onClick={onTrade}
        disabled={!isOpen}
      >
        <IconWrap>
          <Icon
            name={isOpen ? "trade" : "lock"}
            color={isOpen ? "#000" : "#5a6a7a"}
            size={isOpen ? 24 : 20}
          />
        </IconWrap>
        <TabLabel $primary={isOpen} $disabled={!isOpen}>
          {isOpen ? tr("trade", lang) : tr("closed", lang)}
        </TabLabel>
      </TabBtn>
    </Container>
  );
}
